import { cloneElement, useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { Info, SearchX } from "lucide-react";

import { useAuth } from "@/hooks/useAuth";
import { useDebounce } from "@/hooks/useDebounce";
import { hasPermission, getUserPermissions } from "@/permissions/rbac";
import { PERMISSIONS } from "@/permissions/constant";
import { Card } from "@/components/ui/Card";
import { RecordService } from "@/services/recordService";
import { resolvedNotice } from "../notice";

import SearchPanel from "./SearchPanel";
import ResultsTable from "./ResultsTable";
import Dossier from "./Dossier";

const MIN_QUERY_LENGTH = 2;

// The URL a screen with nothing chosen has. Every read below already spells its
// own default (`?? ""`, `?? "candidate"`, `|| 25`), so a bare URL *is* the
// default state and a second list of those values would only drift from it.
const EMPTY_PARAMS = new URLSearchParams();

/**
 * Was this document re-entered — F5, the reload button — rather than navigated
 * to? A paste, a bookmark, a new tab and a link all report "navigate".
 *
 * Absent where the browser implements no navigation timing, which reads as
 * "navigate": a deep link that opens is the better of the two failures.
 */
const navigatedByReload = () => {
  const [entry] = performance?.getEntriesByType?.("navigation") ?? [];
  return entry?.type === "reload";
};

const initialSearch = {
  loading: false,
  kind: null,
  results: [],
  resolved: null,
  degraded: [],
  truncated: false,
  error: null,
  searchedQuery: "",
};

/**
 * The shared Record Lookup flow, driven entirely by the URL:
 * `?q=&type=&section=&page=&per_page=&application_id=&allTenants=`.
 *
 * Three states: a search box (+ subject tabs), a results table, and a
 * dossier. A UUID query short-circuits the results table and jumps straight
 * to the dossier, so a lookup run during an investigation survives being
 * pasted to a colleague.
 */
const RecordLookupScreen = ({ isSuperAdmin = false, basePath, tenantId: ambientTenantId, tenantPicker, allTenants: initialAllTenants = false }) => {
  const setHeader = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  // A reload clears the screen; every other arrival opens the URL it was given.
  //
  // Both are asked for, and they are different arrivals. Pressing F5 on an open
  // dossier should give the search box back. A lookup pasted to a colleague,
  // bookmarked, or linked from the `impact` section of someone else's record
  // has to open what it names — keeping the whole state in the URL is the only
  // reason this screen is shaped this way. Navigation timing is what tells them
  // apart: re-entering the same URL is a "reload", a paste or a link is a
  // "navigate". Where the browser reports neither, the link opens; of the two
  // ways to be wrong, a dead deep link is the one that loses somebody's work.
  //
  // Decided during render rather than in the effect, and read through
  // `activeParams` below, because `useDebounce` seeds itself from the query on
  // the *first* render. Clearing the URL from an effect leaves that one render
  // holding the old query, which is long enough to fire the search the reload
  // was meant to cancel.
  const [enteredByReload] = useState(navigatedByReload);
  // One-shot, and latched rather than derived: it stays true only until the
  // arrival URL has actually been cleared. Derived from `searchParams` alone it
  // re-armed on the very next param write, so after a reload the first thing
  // typed was wiped as though it were the old URL — leaving the screen unable
  // to run a search at all until it was navigated to afresh.
  const [clearingEntry, setClearingEntry] = useState(
    () => enteredByReload && !!searchParams.toString(),
  );
  const activeParams = clearingEntry ? EMPTY_PARAMS : searchParams;

  const q = activeParams.get("q") ?? "";
  const type = activeParams.get("type") ?? "candidate";
  const trimmedQ = q.trim();

  const section = activeParams.get("section") ?? "";
  const page = Number(activeParams.get("page") || 1);
  const perPage = Number(activeParams.get("per_page") || 25);
  const applicationId = activeParams.get("application_id") ?? "";

  // A reload resets the tenant scope along with everything else it resets.
  // Absent means "scoped to the picker" on every other arrival, so a pasted
  // link keeps opening the tenant it names; after F5 the picker returns to All
  // Tenants instead of inheriting whichever tenant `lib/tenantScope` persisted
  // for the session, which left a cleared screen still narrowed to one
  // organisation. A tenant user is scoped by their JWT and never fleet-wide.
  const allTenantsParam = activeParams.get("allTenants");
  const allTenants =
    initialAllTenants ||
    (allTenantsParam === null ? isSuperAdmin && enteredByReload : allTenantsParam === "true");

  // Which tenant the open dossier belongs to. Only ever set from a result row
  // on an all-tenants search — a fleet-wide search finds a candidate in tenant
  // B, and every dossier, section and export call after that has to name B.
  const rowTenantId = activeParams.get("tenantId") ?? "";

  const [searchState, setSearchState] = useState(initialSearch);

  useEffect(() => {
    if (!clearingEntry) return;

    setSearchParams(new URLSearchParams(), { replace: true });
    setSearchState(initialSearch);
    setClearingEntry(false);
  }, [clearingEntry, setSearchParams]);

  const debouncedQ = useDebounce(trimmedQ, 500);

  // A SuperAdmin's permission array is exactly [SUPER_ADMIN_MANAGE] — the
  // login payload carries no dot-notation codes — while `require_permission`
  // clears a SuperAdmin ahead of any code check. Without the first clause the
  // UI hides an Export button the backend would happily serve.
  const canExport = isSuperAdmin || user?.isSuperAdmin || hasPermission(PERMISSIONS.RECORD_EXPORT, getUserPermissions(user));

  /**
   * Apply several URL param changes in one `setSearchParams` call.
   *
   * A single call is important: React Router builds the next search string
   * from the *render-time* `searchParams`, so two calls in the same handler
   * both start from the old URL and the second silently undoes the first
   * (e.g. clicking the User tab set `type=user`, then the trailing
   * `section`/`page` cleanup calls replaced it back with `type=candidate`).
   * Updating `null`/empty/`false` values removes the key.
   */
  const setParams = useCallback(
    (updates) => {
      setSearchParams(
        (params) => {
          Object.entries(updates).forEach(([key, value]) => {
            if (value == null || value === "" || value === false) {
              params.delete(key);
            } else {
              params.set(key, String(value));
            }
          });

          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    setHeader?.({
      title: "Record Lookup",
      subTitle: isSuperAdmin ? "What the platform holds on a person, fleet-wide" : "",
    });

    return () =>
      setHeader?.({
        title: "",
        subTitle: "",
      });
  }, [setHeader, isSuperAdmin]);

  // The tenant the search runs inside. For a SuperAdmin the picker's scope is
  // the default; the all-tenants toggle opts out — and must reach the axios
  // config as `crossTenant: true`, or `@/lib/api` attaches the ambient
  // X-Target-Tenant-Id and the search silently narrows to one tenant.
  const searchTenantId = !allTenants ? ambientTenantId : undefined;

  // A SuperAdmin owns no tenant, so `useTenantScope` fetches the tenant list
  // and picks one a moment after this mounts. A tenant-scoped search sent
  // before that lands names no tenant, and the backend falls back to whichever
  // one the ambient X-Target-Tenant-Id header last carried — a different
  // organisation's records under the picker's own label. Hold the search
  // instead; the effect below re-runs the moment the scope arrives.
  //
  // An all-tenants search is unscoped by design and waits for nothing.
  const scopeReady = !isSuperAdmin || allTenants || !!searchTenantId;

  /**
   * Search whenever the debounced query or search scope changes.
   *
   * `debouncedQ !== trimmedQ` means the visible search value has changed but
   * the debounce has not caught up yet. This matters when switching between
   * Candidate and User: without the guard, the previous Candidate query can
   * briefly be submitted as a User query before the cleared search value
   * reaches the debounce.
   */
  useEffect(() => {
    const query = debouncedQ;

    if (query !== trimmedQ) {
      return;
    }

    if (!scopeReady) {
      return;
    }

    if (query.length < MIN_QUERY_LENGTH) {
      setSearchState(initialSearch);
      return;
    }

    let cancelled = false;

    setSearchState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    RecordService.search({
      q: query,
      type,
      allTenants,
      tenantId: searchTenantId,
    })
      .then((data) => {
        if (cancelled) return;

        setSearchState({
          loading: false,
          kind: data.kind ?? "name",
          results: data.results ?? [],
          resolved: data.resolved ?? null,
          degraded: data.degraded ?? [],
          truncated: !!data.truncated,
          error: null,
          searchedQuery: query,
        });
      })
      .catch((err) => {
        if (cancelled) return;

        setSearchState((prev) => ({
          ...prev,
          loading: false,
          error: err,
          searchedQuery: query,
        }));
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQ, trimmedQ, type, allTenants, searchTenantId, scopeReady]);

  // A UUID search short-circuits into a dossier; carry the resolved
  // application scope into the URL so a reload lands in the same place.
  const resolved = searchState.resolved;

  // The dossier is always scoped to one tenant. Under all-tenants the search
  // itself is unscoped, so the tenant has to come back with the answer: the
  // resolution names it (the backend looked the record up fleet-wide and knows
  // which tenant it found it in), and the clicked row is the fallback for a
  // response that predates that field. Neither, and the dossier call inherits
  // the ambient X-Target-Tenant-Id — whichever tenant the picker last
  // selected, which is not where the record is.
  const dossierTenantId = allTenants ? resolved?.tenantId || rowTenantId || undefined : searchTenantId;

  useEffect(() => {
    if (!resolved) return;

    if (resolved.applicationId && resolved.applicationId !== applicationId) {
      setParams({
        application_id: resolved.applicationId,
      });
    }
  }, [resolved, applicationId, setParams]);

  const dossierSubject = useMemo(() => {
    const r = resolved;

    if (!r) return null;

    if (r.candidateId) {
      return {
        subjectType: "candidate",
        subjectId: r.candidateId,
      };
    }

    if (r.userId) {
      return {
        subjectType: "user",
        subjectId: r.userId,
      };
    }

    return null;
  }, [resolved]);

  // Every kind but "uuid" returns a result list — an email or a phone number
  // classifies as its own kind and still comes back with hits (an old address
  // that only survives on an application snapshot is exactly that case). Only
  // a UUID short-circuits straight into the dossier.
  const showResults = !!searchState.kind && searchState.kind !== "uuid" && !dossierSubject && !!searchState.searchedQuery;

  const handleSelectRow = useCallback(
    (row) => {
      if (!row?.subjectId) return;

      // Re-issue the search with the subject's id: the backend classifies it
      // as a UUID and the dossier opens — same path as pasting the id.
      setSearchParams(
        (params) => {
          params.set("q", String(row.subjectId));

          params.delete("application_id");
          params.delete("section");
          params.delete("page");

          if (row.tenantId) {
            params.set("tenantId", String(row.tenantId));
          } else {
            params.delete("tenantId");
          }

          if (!isSuperAdmin) {
            params.set("type", row.subject ?? "candidate");
          }

          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams, isSuperAdmin],
  );

  /**
   * Switching between Candidate and User starts a completely fresh lookup.
   *
   * Person-specific state must never carry between subject types:
   * - clear the visible query
   * - clear previous search results
   * - close an open dossier
   * - clear application scope
   * - clear section/page navigation
   *
   * Tenant scope and per-page preference are intentionally preserved.
   */
  const handleTypeChange = useCallback(
    (nextType) => {
      if (nextType === type) return;

      // Clear results/resolved immediately so an open dossier disappears
      // without waiting for the debounced search effect.
      setSearchState(initialSearch);

      setParams({
        type: nextType,
        q: null,
        application_id: null,
        section: null,
        page: null,
        tenantId: null,
      });
    },
    [type, setParams],
  );

  const handleSectionChange = useCallback(
    (key) => {
      setParams({
        section: key,
        page: null,
      });
    },
    [setParams],
  );

  const handlePageChange = useCallback(
    (next) => {
      setParams({
        page: next > 1 ? next : null,
      });
    },
    [setParams],
  );

  const handlePerPageChange = useCallback(
    (size) => {
      setParams({
        per_page: size,
        page: null,
      });
    },
    [setParams],
  );

  const validationMessage = trimmedQ.length > 0 && trimmedQ.length < MIN_QUERY_LENGTH ? "Type at least 2 characters to search." : null;

  const searchError = searchState.error;

  // A network failure, a timeout or a 500 with an empty body all leave the
  // envelope undefined. Without the fallback the notice never renders and the
  // screen reads as if nothing had been asked.
  const searchErrorMessage = searchError ? searchError.response?.data?.message || "The search could not be completed. Check your connection and try again." : null;

  /**
   * A pasted id that opened no record. Three different answers hide behind
   * that, and `../notice` is the one place that words them — including the one
   * QA filed: an id that is a person of the kind the *other* tab searches.
   * Without a sentence here the screen falls back to the intro card and
   * answers nothing.
   */
  const uuidNotice = useMemo(() => {
    if (searchState.loading || searchError || searchState.kind !== "uuid" || !searchState.searchedQuery || dossierSubject) {
      return null;
    }

    return resolvedNotice({
      resolved,
      type,
      isSuperAdmin,
      degraded: searchState.degraded,
    });
  }, [searchState.loading, searchState.kind, searchState.searchedQuery, searchState.degraded, searchError, resolved, dossierSubject, isSuperAdmin, type]);

  // One notice, one place. A failed search and an id that resolves to nothing
  // are the same thing to the reader: the screen could not answer.
  const noticeMessage = searchErrorMessage || uuidNotice;

  /**
   * Keep the SuperAdmin tenant dropdown and the all-tenants toggle in step:
   * both feed the same `allTenants` URL flag. The dropdown shows "All
   * Tenants" while the toggle is on, and picking it (or a specific tenant)
   * flips the flag the same way the checkbox would.
   */
  const tenantPickerNode = useMemo(() => {
    if (!tenantPicker) return null;

    return cloneElement(tenantPicker, {
      tenantId: allTenants ? "" : ambientTenantId,

      onChange: (next) => {
        if (next === "ALL_TENANTS" || next === "") {
          setParams({
            allTenants: true,
          });
        } else {
          setParams({
            allTenants: "false",
          });

          tenantPicker.props.onChange?.(next);
        }
      },
    });
  }, [tenantPicker, allTenants, ambientTenantId, setParams]);

  return (
    <div className="flex w-full flex-col gap-4 pb-6">
      <SearchPanel
        value={q}
        onValueChange={(next) =>
          setParams({
            q: next,
            tenantId: null,
          })
        }
        type={type}
        onTypeChange={handleTypeChange}
        isSuperAdmin={isSuperAdmin}
        allTenants={allTenants}
        onAllTenantsChange={(next) => {
          setParams({
            allTenants: next ? true : "false",
          });
        }}
        trailing={tenantPickerNode}
      />

      {validationMessage && <p className="px-1 text-[12px] text-text-secondary">{validationMessage}</p>}

      {!dossierSubject && !showResults && !validationMessage && !noticeMessage && (
        <Card className="flex w-full flex-col items-center gap-2 rounded-xl border border-border-primary bg-background-secondary px-6 py-14 text-center">
          <Info size={20} className="text-text-muted" aria-hidden="true" />

          <p className="text-[13px] font-medium text-text-primary">Search for a candidate or an employee</p>

          <p className="max-w-md text-[12px] leading-5 text-text-secondary">
            By name, email, phone or id. A pasted id opens the full record directly — everything the platform holds, section by section, and one export that bundles it
            all.
          </p>
        </Card>
      )}

      {noticeMessage && !dossierSubject && (
        <div className="flex items-start gap-2.5 rounded-lg border border-tone-rose/30 bg-tone-rose-soft px-3.5 py-2.5">
          <SearchX size={15} className="mt-0.5 shrink-0 text-tone-rose" aria-hidden="true" />

          <p className="text-[12px] leading-5 text-text-primary">{noticeMessage}</p>
        </div>
      )}

      {!dossierSubject && showResults && (
        <ResultsTable
          results={searchState.results}
          loading={searchState.loading}
          degraded={searchState.degraded}
          truncated={searchState.truncated}
          isSuperAdmin={isSuperAdmin}
          onSelectRow={handleSelectRow}
          query={searchState.searchedQuery}
        />
      )}

      {dossierSubject && (
        <Dossier
          subjectType={dossierSubject.subjectType}
          subjectId={dossierSubject.subjectId}
          applicationId={applicationId}
          tenantId={dossierTenantId}
          basePath={basePath}
          canExport={canExport}
          section={section}
          onSectionChange={handleSectionChange}
          page={page}
          onPageChange={handlePageChange}
          perPage={perPage}
          onPerPageChange={handlePerPageChange}
          onRemoveScope={() =>
            setParams({
              application_id: null,
            })
          }
        />
      )}
    </div>
  );
};

export default RecordLookupScreen;